import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { isImmutable, fromJS } from "immutable";
import { CSSTransition } from "react-transition-group";
import camelCase from "lodash/camelCase";

import "assets/stylesheets/transition.css";
import "assets/stylesheets/Tooltip.css";

import { randomStrGenerator } from "utils.js";

import { apiBaseUrl } from "apis/apiHelpers.js";

import { getGlobalMenu } from "actions/globalMenuActions.js";
import { logoutCurrentUser } from "actions/currentUserActions.js";

import {
  isLoadingSelector,
  hasErrorsSelector,
  globalMenuDataSelector
} from "selectors/globalMenuSelector.js";
import { currentUserDataSelector } from "selectors/currentUserSelector";
const getMegaMenuSkeleton = () => (
  <div style={{ height: "60px", width: "100vw", backgroundColor: "#222f3e" }} />
);

const handleScriptLoad = () => {
  // wait for animation to complete and ensure DOM is ready
  setTimeout(() => {
    try {
      // Check if the required DOM elements exist before calling onLoad
      if (window.webslidemenu && window.webslidemenu.onLoad) {
        // Check if the required DOM elements exist
        const body = document.body;
        const head = document.head;

        if (!body || !head) {
          // Required DOM elements (body/head) not available yet, retrying...
          // Retry after a short delay
          setTimeout(() => handleScriptLoad(), 500);
          return;
        }

        // Ensure DOM is ready and elements exist
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => {
            try {
              window.webslidemenu.onLoad();
            } catch (error) {
              // Error calling webslidemenu.onLoad after DOM ready
            }
          });
        } else {
          // DOM is already ready
          try {
            window.webslidemenu.onLoad();
          } catch (error) {
            // If it fails, try again after a delay
            setTimeout(() => {
              try {
                window.webslidemenu.onLoad();
              } catch (retryError) {
                // Error calling webslidemenu.onLoad on retry
              }
            }, 1000);
          }
        }
      }
    } catch (error) {
      // Error loading webslidemenu
    }
  }, 1000); // Increased timeout to ensure DOM is fully ready
};

const GlobalMenuContainer = () => {
  const dispatch = useDispatch();

  const isLoading = useSelector(isLoadingSelector);
  const hasErrors = useSelector(hasErrorsSelector);
  const globalMenuJson = useSelector(globalMenuDataSelector);
  const currentUser = useSelector(currentUserDataSelector);
  window.currentUser_id = currentUser.get("id");

  useEffect(() => {
    dispatch(getGlobalMenu());
  }, [dispatch]);

  useEffect(() => {
    // Retailer ID tooltip: match Rails behavior (hover on desktop, tap on touch)
    // NOTE: The React-rendered menu markup doesn't always preserve
    // `data-retailer-id-tooltip` as an attribute (it can show up as a nested
    // `<data-retailer-id-tooltip>` element instead). So we primarily target the
    // icon in the Retailer ID header block.
    const selector =
      ".header-retailer-id .fa-question-circle, " +
      ".header-retailer-id__icon, " +
      ".header-retailer-id_icon, " +
      ".header-retailer-id_";
    const isTouchDevice =
      "ontouchstart" in window ||
      (navigator &&
        (navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0)) ||
      (window.matchMedia && window.matchMedia("(pointer: coarse)").matches);

    let tooltipEl = null;
    let currentTarget = null;
    const originalTitleByEl = new WeakMap();

    const hideTooltip = () => {
      if (tooltipEl && tooltipEl.parentNode) tooltipEl.parentNode.removeChild(tooltipEl);
      tooltipEl = null;
      currentTarget = null;
    };

    const showTooltip = (target) => {
      // Prevent native browser tooltip (single-line) from showing underneath.
      if (!originalTitleByEl.has(target)) {
        originalTitleByEl.set(target, target.getAttribute("title") || "");
      }
      const raw = originalTitleByEl.get(target);
      if (!raw) return;

      // Some runtimes normalize whitespace in `title` and drop newlines (sometimes
      // even removing the whitespace around where the newline was). To keep tooltip
      // sizing consistent across locales, re-introduce a line break when needed.
      // This is strictly scoped to this specific tooltip message.
      let text = raw.replace(/\s+/g, " ").trim();
      const looksLikeRetailerIdTooltip =
        /provide id/i.test(text) ||
        /enable linking/i.test(text) ||
        /pour permettre/i.test(text) ||
        /para permitir/i.test(text) ||
        /um die/i.test(text) ||
        /連携/i.test(text);

      if (looksLikeRetailerIdTooltip && !text.includes("\n")) {
        // Prefer inserting before the second-clause marker (works across locales)
        const markerMatch = text.match(
          /(to enable|pour|para|um|連携)/i
        );
        if (markerMatch && markerMatch.index && markerMatch.index > 0) {
          text =
            text.slice(0, markerMatch.index).trimEnd() +
            "\n" +
            text.slice(markerMatch.index).trimStart();
        } else {
          // Fallback: split near the middle. If there are no spaces at all (e.g. "marquespour"),
          // split at a character boundary to avoid the tooltip becoming excessively wide.
          const mid = Math.floor(text.length / 2);
          const leftSpace = text.lastIndexOf(" ", mid);
          const rightSpace = text.indexOf(" ", mid);
          const splitAt =
            leftSpace > 0 ? leftSpace : rightSpace > 0 ? rightSpace : mid;
          text = `${text.slice(0, splitAt).trim()}\n${text.slice(splitAt).trim()}`;
        }
      }

      // Toggle if tapping the same icon again
      if (currentTarget === target && tooltipEl) {
        hideTooltip();
        return;
      }

      hideTooltip();
      currentTarget = target;

      tooltipEl = document.createElement("div");
      tooltipEl.className = "header-retailer-tooltip-custom";
      tooltipEl.setAttribute("role", "tooltip");
      tooltipEl.textContent = text;

      document.body.appendChild(tooltipEl);
      // Clear title to avoid native tooltip flashing on hover.
      target.setAttribute("title", "");

      // Position beneath the icon, clamped within viewport.
      const rect = target.getBoundingClientRect();
      const tipRect = tooltipEl.getBoundingClientRect();
      const top = Math.min(window.innerHeight - tipRect.height - 8, rect.bottom + 8);
      const left = Math.max(
        8,
        Math.min(
          window.innerWidth - tipRect.width - 8,
          rect.left + (rect.width - tipRect.width) / 2
        )
      );
      tooltipEl.style.top = `${top}px`;
      tooltipEl.style.left = `${left}px`;
    };

    const hideAndRestoreTitle = () => {
      if (currentTarget && originalTitleByEl.has(currentTarget)) {
        currentTarget.setAttribute("title", originalTitleByEl.get(currentTarget));
      }
      hideTooltip();
    };

    const onDocClickCapture = (e) => {
      const target = e.target.closest(selector);
      if (!target) {
        hideAndRestoreTitle();
        return;
      }
      if (isTouchDevice) {
        e.preventDefault();
        e.stopPropagation();
        showTooltip(target);
      }
    };

    const onMouseEnter = (e) => {
      const target = e.target.closest(selector);
      if (!target) return;
      if (isTouchDevice) return;
      showTooltip(target);
    };

    const onMouseLeave = (e) => {
      const target = e.target.closest(selector);
      if (!target) return;
      if (isTouchDevice) return;
      hideAndRestoreTitle();
    };

    const onFocusIn = (e) => {
      const target = e.target.closest(selector);
      if (!target) return;
      if (isTouchDevice) return;
      showTooltip(target);
    };

    const onFocusOut = (e) => {
      const target = e.target.closest(selector);
      if (!target) return;
      if (isTouchDevice) return;
      hideAndRestoreTitle();
    };

    document.addEventListener("click", onDocClickCapture, true);
    document.addEventListener("mouseover", onMouseEnter, true);
    document.addEventListener("mouseout", onMouseLeave, true);
    document.addEventListener("focusin", onFocusIn, true);
    document.addEventListener("focusout", onFocusOut, true);
    return () => {
      document.removeEventListener("click", onDocClickCapture, true);
      document.removeEventListener("mouseover", onMouseEnter, true);
      document.removeEventListener("mouseout", onMouseLeave, true);
      document.removeEventListener("focusin", onFocusIn, true);
      document.removeEventListener("focusout", onFocusOut, true);
      hideAndRestoreTitle();
    };
  }, []);

  useEffect(() => {
    window.handleLogout = () => {
      dispatch(logoutCurrentUser());
    };

    const handleLogoutClick = (event) => {
      if (event.target.textContent === "Logout" ||
        event.target.closest('a')?.textContent === "Logout") {
        event.preventDefault();
        window.handleLogout();
      }
    };

    document.addEventListener('click', handleLogoutClick);

    return () => {
      document.removeEventListener('click', handleLogoutClick);
      delete window.handleLogout;
    };
  }, [dispatch]);

  useEffect(() => {
    if (isLoading === false && !hasErrors) {
      // Load Headway widget only if account is configured
      const headwayAccount = process.env.REACT_APP_HEADWAY_ACCOUNT;
      if (headwayAccount) {
        const script = document.createElement("script");
        script.async = true;
        script.src = "https://cdn.headwayapp.co/widget.js";
        script.onload = function () {
          try {
            const config = {
              selector: ".headway_widget",
              account: headwayAccount
            };
            setTimeout(() => {
              if (window.Headway && window.Headway.init) {
                window.Headway.init(config);
              }
            }, 500);
          } catch (error) {
            // Headway initialization failed
          }
        };
        script.onerror = function () {
          // Failed to load Headway widget script
        };
        document.head.appendChild(script);
      }

      // Load javascript required to handle modal dialogs of global menu
      // Check if script is already loaded to prevent duplicates
      const existingScript = document.querySelector(`script[src="${apiBaseUrl}/webslidemenu/index.js"]`);
      const isWebslideMenuLoaded = window.webslidemenu && window.webslidemenu.onLoad;

      if (!existingScript && !isWebslideMenuLoaded) {
        let scriptTag = document.createElement("script");
        scriptTag.type = "text/javascript";
        scriptTag.src = `${apiBaseUrl}/webslidemenu/index.js`;
        scriptTag.async = true;
        scriptTag.onload = function () {
          handleScriptLoad();
        };
        scriptTag.onerror = function () {
          // Failed to load webslidemenu script
        };
        document.body.appendChild(scriptTag);

        return () => {
          try {
            if (scriptTag && scriptTag.parentNode) {
              document.body.removeChild(scriptTag);
            }
          } catch (error) {
            // Error removing script tag
          }
        };
      } else {
        if (isWebslideMenuLoaded) {
          // If webslidemenu is already loaded, try to call onLoad
          handleScriptLoad();
        }
      }
    }
  }, [isLoading, hasErrors]);

  return (
    <>
      {isLoading && getMegaMenuSkeleton()}

      {hasErrors && getMegaMenuSkeleton()}

      <CSSTransition
        classNames="fade"
        timeout={450}
        in={!isLoading && !hasErrors}
        unmountOnExit
      >
        <header>{buildMenuTree(globalMenuJson)}</header>
      </CSSTransition>
    </>
  );
};

const attributes = [
  "class",
  "id",
  "href",
  "src",
  "height",
  "width",
  "style",
  "target",
  "aria-haspopup",
  "wrap-tag",
  "data-id",
  "data-name",
  "data-dismiss",
  "data-toggle",
  "data-method",
  "data-rails-domain",
  "data-dynamic-url",
  "data-static-url",
  "data-url",
  "data-display-name",
  "rel",
  "__content__",
  "role",
  "title",
  "input",
  "action",
  "method",
  "name",
  "type",
  "value",
  "disabled",
  "aria-expanded",
  "defaultvalue",
  "data-content"
];

// Add the text content for <option> tag from webslidemenu/index.js on rails side.
const elementsWithoutChildren = ["img", "hr", "option"];

const createHref = href => {
  return (
    href &&
    (href.startsWith("#") ||
      href.startsWith("http") ||
      href.startsWith("mailto:")
      ? href
      : `${apiBaseUrl}${href}`)
  );
};

const createSrc = src => {
  return src && `${apiBaseUrl}${src}`;
};

const createStylesHsh = stylesStr => {
  let stylesHsh = {};

  if (stylesStr) {
    stylesStr.split("; ").forEach(styleStr => {
      const [styleKey, styleValue] = styleStr.split(": ");

      stylesHsh[camelCase(styleKey)] = styleValue.replace(";", "");
    });
  }

  return stylesHsh;
};

const createAttrsHash = attrsObj => {
  const ignoreList = ["href", "src", "style", "class", "defaultvalue"];

  let attrsHash = {
    key: randomStrGenerator(),
    href: createHref(attrsObj.get("href")),
    src: createSrc(attrsObj.get("src")),
    style: createStylesHsh(attrsObj.get("style")),
    className: attrsObj.get("class"),
    defaultValue: attrsObj.get("defaultvalue")
  };

  attributes.forEach(attr => {
    if (!ignoreList.includes(attr)) {
      if (attr.startsWith("data_")) {
        attrsHash[attr.replace("_", "-")] = attrsObj.get(attr);
      } else {
        attrsHash[attr] = attrsObj.get(attr);
      }
    }
  });

  return attrsHash;
};

const createReactElm = (parentElm, obj) => {
  let textContent;

  if (!isImmutable(obj)) {
    if (typeof obj !== "string") {
      return null;
    } else {
      textContent = obj;
      obj = fromJS({});
    }
  }
  return React.createElement(
    parentElm,
    createAttrsHash(obj),
    textContent
      ? textContent
      : elementsWithoutChildren.includes(parentElm)
        ? null
        : buildMenuTree(obj)
  );
};

const buildMenuTree = json => {
  return json
    .filterNot((_, key) => attributes.includes(key))
    .entrySeq()
    .map(([elm, attrs]) => {
      if (attrs === null) {
        return React.createElement(elm, { key: randomStrGenerator() });
      } else if (attrs._root === null) {
        return attrs.map(obj => {
          return createReactElm(elm, obj);
        });
      } else {
        return createReactElm(elm, attrs);
      }
    });
};

export default React.memo(GlobalMenuContainer);
